/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import {
  DeliveryType,
  type Path,
  type PaymentMethodCode,
  type Service,
  type ServiceWithEstimate,
} from '@ahm/api-wrappers-core';
import { formatCurrency, generateLetterAvatar } from '@ahm/common-helpers';
import { useAuth } from '@ahm/react-auth';
import { useLocationStore } from '@ahm/react-map/store';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Group,
  Skeleton,
  Stack,
  Text,
  Title,
  rem,
} from '@mantine/core';
import { useMutationState } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { type JSX, useCallback, useEffect, useMemo } from 'react';
import { useGetServiceTypes } from '../../api/service';
import BookingServiceInfoTooltip from './booking-service-info-tooltip';
import classes from './services.module.css';
import SurgeDownIcon from '@/assets/icons/surge-down-icon.svg';
import SurgeUpIcon from '@/assets/icons/surge-up-icon.svg';
import { ROUTES } from '@/config/router';
import { useRemoteConfigHook } from '@/hooks';
import { ActionIndexPath } from '@/lib/constants';
import { getServiceType } from '@/lib/utils';
import { useLocaleSuffix, usePathname } from '@/navigation';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

type Props = {
  enabled?: boolean;
};

type UseBookingServiceReturn = {
  isLoading: boolean;
  isDisabled: boolean;
  estimatedServices: ServiceWithEstimate[];
  selectedService: Service | null;
  pickupPath: Path | null;
  onOpenPopupAllService: () => void;
  onClosePopupAllService: () => void;
  renderEstimatedServices: () => JSX.Element | JSX.Element[] | null;
};

function processServiceByPlatform(
  service: Service,
  user: any,
  webInAppConfig: any
): Service | null {
  const webInAppId = webInAppConfig?.id;
  if (webInAppId) {
    const enablePlatform = service.enable_platform ?? [];
    if (enablePlatform.length > 0 && !enablePlatform.includes(webInAppId)) {
      return null;
    }
  }

  const payload: Service = {
    ...service,
    payment_method: [] as { payment: PaymentMethodCode }[],
  };

  // Set max_cod limits
  if (user?.verified && user?.max_cod) {
    payload.max_cod_per_stop_point = user.max_cod || service.max_cod;
    payload.max_cod = service.max_cod;
  } else if (webInAppId && !user?.verified) {
    const maxCod = webInAppConfig.maxCod ?? service.max_cod;
    payload.max_cod_per_stop_point = maxCod;
    payload.max_cod = maxCod;
  } else {
    payload.max_cod_per_stop_point = service.max_cod;
    payload.max_cod = service.max_cod;
  }

  // Set payment methods
  if (webInAppConfig?.paymentMethods) {
    payload.payment_method = webInAppConfig.paymentMethods.map((methods) => ({
      payment: methods,
    }));
  }

  return payload;
}

export function useBookingService({ enabled }: Props): UseBookingServiceReturn {
  const localeSuffix = useLocaleSuffix();
  const pathname = usePathname();

  const { isBikeService, isHMService } = useMemo(
    () => getServiceType(pathname),
    [pathname]
  );

  const selectedService = useBookingStore((state) => state.selectedService);
  const setSelectedService = useBookingStore(
    (state) => state.setSelectedService
  );
  const serviceTypes = useBookingStore((state) => state.serviceTypes);
  const setServiceTypes = useBookingStore((state) => state.setServiceTypes);
  const setIsEstimateAllService = useBookingStore(
    (state) => state.setIsEstimateAllService
  );
  const estimatedAllService = useBookingStore(
    (state) => state.estimatedAllService
  );
  const setOpenPopupAllService = useBookingStore(
    (state) => state.setOpenPopupAllService
  );
  const kaProfile = useBookingStore((state) => state.kaProfile);
  const setKaProfile = useBookingStore((state) => state.setKaProfile);
  const setIsSuggestPromotion = useBookingStore(
    (state) => state.setIsSuggestPromotion
  );

  const paths = useLocationStore.use.paths();
  const loadingLocation = useLocationStore.use.loading();
  const { session, webInAppConfig } = useAuth();
  const user = session?.user;
  const { surgePriceConfig } = useRemoteConfigHook();

  const searchParams = useSearchParams();
  const loadServiceParam = searchParams.get('load_service');

  const isPendingIntercity = useMutationState({
    filters: { status: 'pending', mutationKey: ['estimateIntercityOrderFn'] },
    select: (mutation) => mutation.state.status === 'pending',
  });

  const isPendingEstimateOrder = useMutationState({
    filters: { status: 'pending', mutationKey: ['estimateNewOrderFn'] },
    select: (mutation) => mutation.state.status === 'pending',
  });

  const hasPendingMutation =
    isPendingIntercity.length > 0 || isPendingEstimateOrder.length > 0;

  const pickupPath: Path | null = useMemo(() => {
    return paths[ActionIndexPath.Pickup] ?? null;
  }, [paths]);

  const currentDeliveryType = useMemo(() => {
    switch (pathname) {
      // case ROUTES.houseMoving:
      //   return DeliveryType.HouseMoving;
      case ROUTES.nationwide:
        return DeliveryType.Nationwide;
      case ROUTES.truck:
        return DeliveryType.Truck;
      case ROUTES.rental:
        return DeliveryType.Rental;
      default:
        return;
    }
  }, [pathname]);

  const {
    data: serviceTypesData,
    isLoading: isLoadingServiceTypes,
    error: serviceTypesError,
  } = useGetServiceTypes(
    {
      lat: Number(pickupPath?.lat),
      lng: Number(pickupPath?.lng),
      delivery_type: currentDeliveryType,
      user_mobile: (loadServiceParam && user?._id) ?? undefined, // for Onwheel miniapp
    },
    {
      enabled: Boolean(enabled && user && pickupPath?.lat && pickupPath?.lng),
      staleTime: 10 * 1000,
    }
  );

  const estimatedServices = useMemo((): ServiceWithEstimate[] => {
    if (!serviceTypes?.length) return [];
    if (paths.length < 2 || !estimatedAllService?.length) return serviceTypes;

    const estimateMap = new Map(
      estimatedAllService.map((data) => [data.service_id, data])
    );

    return serviceTypes.map((service) => {
      const matchedEstimate = estimateMap.get(service._id);

      if (!matchedEstimate) return service;
      if (service.estimate_data === matchedEstimate) return service;

      return {
        ...service,
        estimate_data: matchedEstimate as ServiceWithEstimate['estimate_data'],
      };
    });
  }, [serviceTypes, estimatedAllService, paths.length]);

  const estimatedServicesGroup: Record<string, ServiceWithEstimate[]> | null =
    useMemo(() => {
      if (!estimatedServices.length) return null;

      const groupNameKey = `group_name_${localeSuffix}` as const;
      const grouped: Record<string, ServiceWithEstimate[]> = {};
      const others: ServiceWithEstimate[] = [];

      for (const item of estimatedServices) {
        const key =
          item[groupNameKey] ||
          (item.group_id === 'SAAS' ? item.group_id : null);

        if (key) {
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(item);
        } else {
          others.push(item);
        }
      }

      if (others.length > 0) {
        grouped.Others = others;
      }

      return grouped;
    }, [estimatedServices, localeSuffix]);
  useEffect(() => {
    if (user?.partner && user.partner !== 'ahamove') {
      setKaProfile();
    }
  }, [user?.partner, setKaProfile]);

  useEffect(() => {
    if (isLoadingServiceTypes) return;

    if (serviceTypesError) {
      setSelectedService(null);
      return;
    }

    if (!serviceTypesData) return;

    const servicesByPlatform: Service[] = [];

    for (const service of serviceTypesData) {
      const processed = processServiceByPlatform(service, user, webInAppConfig);
      if (processed) {
        servicesByPlatform.push(processed);
      }
    }

    if (kaProfile?.pinned_services) {
      const existingIds = new Set(servicesByPlatform.map((s) => s._id));
      const pinnedToAdd = kaProfile.pinned_services.filter(
        (pinned) => !existingIds.has(pinned._id)
      );
      servicesByPlatform.push(...(pinnedToAdd as unknown as Service[]));
    }

    if (servicesByPlatform.length > 0) {
      setServiceTypes(servicesByPlatform, pathname);
    }
  }, [
    serviceTypesData,
    isLoadingServiceTypes,
    serviceTypesError,
    pathname,
    kaProfile?.pinned_services,
    user,
    webInAppConfig,
    setServiceTypes,
    setSelectedService,
  ]);

  const getSurgeIcon = useCallback(
    (service: ServiceWithEstimate) => {
      if (
        !surgePriceConfig ||
        service.estimate_data?.error ||
        !service.estimate_data?.data?.surge_rate
      ) {
        return null;
      }

      const currentSurgeRate = service.estimate_data.data.surge_rate;
      const config = surgePriceConfig.find(
        (item) => item.delivery_type === service.delivery_type
      );

      if (!config) return null;

      if (
        config.surge_rate_decrease &&
        currentSurgeRate < config.surge_rate_decrease
      ) {
        return <SurgeDownIcon />;
      }
      if (
        config.surge_rate_increase &&
        currentSurgeRate > config.surge_rate_increase
      ) {
        return <SurgeUpIcon />;
      }

      return null;
    },
    [surgePriceConfig]
  );

  const handleSelectService = useCallback(
    (service: Service) => {
      if (!service?._id) return;
      setSelectedService(service);
      setIsSuggestPromotion(true);
      setOpenPopupAllService(false);
      setIsEstimateAllService(false);
    },
    [
      setSelectedService,
      setIsSuggestPromotion,
      setOpenPopupAllService,
      setIsEstimateAllService,
    ]
  );

  const handleOpenAllService = useCallback(() => {
    setOpenPopupAllService(true);
    if (paths.length >= 2) {
      setIsEstimateAllService(true);
    }
  }, [paths.length, setOpenPopupAllService, setIsEstimateAllService]);

  const handleClose = useCallback(() => {
    setOpenPopupAllService(false);
    setIsEstimateAllService(false);
  }, [setOpenPopupAllService, setIsEstimateAllService]);

  const renderEstimatedServices = useCallback(() => {
    if (
      !estimatedServicesGroup ||
      !Object.keys(estimatedServicesGroup).length ||
      (!isBikeService && !isHMService)
    ) {
      return null;
    }

    return (
      <Stack gap={rem(24)} px={rem(16)} py={rem(12)}>
        {Object.entries(estimatedServicesGroup).map(([groupName, services]) => (
          <Stack gap="sm" key={groupName}>
            <Title order={4}>{groupName}</Title>
            <Stack gap="xs">
              {services.map((s) => {
                const isServiceSelected = selectedService?._id === s._id;
                const hasError = Boolean(s.estimate_data?.error);
                const estimateData = s.estimate_data?.data;

                return (
                  <Button
                    key={s._id}
                    classNames={{
                      root: classes.button,
                      section: classes.buttonSection,
                    }}
                    fullWidth
                    variant="outline"
                    color={isServiceSelected ? 'primary.6' : 'neutral.0'}
                    mih={rem(66)}
                    h="auto"
                    radius={rem(8)}
                    p="sm"
                    onClick={() => handleSelectService(s)}
                    disabled={hasError}
                    bg={isServiceSelected ? 'primary.0' : undefined}
                    styles={{
                      root: {
                        position: 'relative',
                        border: `1px solid ${isServiceSelected ? 'primary.6' : 'neutral.6'} !important`,
                      },
                      inner: { justifyContent: 'flex-start' },
                      label: {
                        width: '100%',
                        flex: '1 1 auto',
                        whiteSpace: 'pre-line',
                      },
                      section: { alignSelf: 'unset' },
                    }}
                    leftSection={
                      <Avatar
                        src={s.icon_url}
                        variant="transparent"
                        radius="sm"
                        size={rem(40)}
                        alt={s?.[`name_${localeSuffix}`]}
                      >
                        {generateLetterAvatar(s?.[`name_${localeSuffix}`])}
                      </Avatar>
                    }
                    rightSection={
                      <Box>
                        {estimateData ? (
                          <Group wrap="nowrap" gap={rem(4)} align="center">
                            {getSurgeIcon(s)}
                            <Stack gap={4}>
                              <Title
                                order={5}
                                c={
                                  isServiceSelected ? 'primary.5' : 'neutral.7'
                                }
                              >
                                {formatCurrency(estimateData.total_price)}
                              </Title>
                              {estimateData.subtotal_price >
                                estimateData.total_price && (
                                <Text size="sm" c="neutral.5" td="line-through">
                                  {formatCurrency(estimateData.subtotal_price)}
                                </Text>
                              )}
                            </Stack>
                          </Group>
                        ) : hasPendingMutation ? (
                          <Skeleton width="100%" height={rem(16)} />
                        ) : null}
                      </Box>
                    }
                  >
                    <Stack gap={rem(2)} w="100%" justify="flex-start">
                      <Group w="100%" gap={rem(4)}>
                        <Group wrap="nowrap" mih={0} gap={rem(4)} flex={1}>
                          <Title
                            ta="left"
                            order={4}
                            fw={600}
                            c="neutral.7"
                            lineClamp={1}
                            style={{ minWidth: 0 }}
                          >
                            {s[`name_${localeSuffix}`]}
                          </Title>
                          <Box style={{ flexShrink: 0 }}>
                            <BookingServiceInfoTooltip
                              service={s}
                              onChangeService={handleSelectService}
                            />
                          </Box>
                        </Group>
                        {s?.badge && (
                          <Badge
                            tt="capitalize"
                            size="md"
                            radius="xl"
                            bg={s.badge.background_color ?? 'red.6'}
                            c={s.badge.text_color ?? 'white'}
                          >
                            {s.badge[`description_${localeSuffix}`] || 'Mới'}
                          </Badge>
                        )}
                      </Group>
                      {hasError ? (
                        <Text c="red.6" size="sm" fw={500} ta="left">
                          {s.estimate_data.error.description}
                        </Text>
                      ) : (
                        <Text
                          c="neutral.6"
                          size="sm"
                          fw={500}
                          ta="left"
                          lineClamp={1}
                        >
                          {s[`description_${localeSuffix}`]}
                        </Text>
                      )}
                    </Stack>
                    {isServiceSelected && (
                      <Box
                        hiddenFrom="sm"
                        pos="absolute"
                        top={0}
                        left={0}
                        bottom={0}
                        h="100%"
                        w={rem(4)}
                        bg="primary.6"
                      />
                    )}
                  </Button>
                );
              })}
            </Stack>
          </Stack>
        ))}
      </Stack>
    );
  }, [
    estimatedServicesGroup,
    selectedService?._id,
    hasPendingMutation,
    isBikeService,
    localeSuffix,
    getSurgeIcon,
    handleSelectService,
  ]);

  return {
    isDisabled: isLoadingServiceTypes || !pickupPath || !serviceTypesData,
    isLoading: isLoadingServiceTypes || loadingLocation,
    estimatedServices,
    selectedService,
    pickupPath,
    onOpenPopupAllService: handleOpenAllService,
    onClosePopupAllService: handleClose,
    renderEstimatedServices,
  };
}
